import React from "react"
import { useState, useEffect, useRef } from "react"
import CurrencyInput from "react-currency-input-field"
import type FieldProps from "../textFieldProps"
import { InputTypes } from "../textFieldProps"
import { getResultColorClass } from "../utilites/get-color-scoring"

const MoneyField = ({ model, setModel, campoId, placeholder, type, required, onBlur, min, max, campoIdRespuesta, editable, title, tooltip, allowZero = false }: FieldProps & { allowZero?: boolean }) => {
    const [resumeValue, setResumeValue] = useState<any>(undefined)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (campoIdRespuesta && model.resume && model.resume[campoIdRespuesta] !== undefined) {
        setResumeValue(model.resume[campoIdRespuesta])
        }
    }, [model, campoIdRespuesta])

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (onBlur) onBlur(e)
    }

    const [isEmpty, setIsEmpty] = useState(
        model[campoId] === undefined || model[campoId] === null || model[campoId] === "",
    )

    useEffect(() => {
        setIsEmpty(model[campoId] === undefined || model[campoId] === null || model[campoId] === "")
    }, [model, campoId])

  return (
    <div className="w-full relative bg-white">
      {title && (
        <div className="flex mb-3">
          <span className="text-md text-[#4A5568] font-medium">{title}</span>
          {tooltip && (
            <div className="relative group inline-block ml-1 mt-1">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="cursor-pointer" >
                  <path d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className="absolute z-50 hidden group-hover:block top-full mt-2 left-40 text-center -translate-x-1/2 px-3 py-2 bg-[#171923] text-white text-sm rounded-lg shadow-lg max-w-[412px] w-max break-words" >
                {tooltip}
              </div>
            </div>
          )}
          <div className="flex ml-2.5 items-center">
            {resumeValue !== undefined && (
              <div className={`${typeof resumeValue === "number" ? getResultColorClass(resumeValue) : "bg-[#EDF2F7] text-[#4A5568]"} w-auto px-1 h-[16px] text-center rounded-[2px] text-xs font-normal`}>
                  {typeof resumeValue === "number" ? resumeValue.toFixed(1) : resumeValue}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="relative w-full">
          {type === InputTypes.Money && (
          <FormatInput
              campoId={campoId}
              model={model}
              setModel={setModel}
              placeholder={placeholder}
              type={type}
              onBlur={handleBlur}
              required={required}
              min={min}
              max={max}
              editable={editable}
              allowZero={allowZero}
              ref={inputRef}
          />
          )}
          <label 
              htmlFor={campoId} 
              className={`absolute left-3 text-[12px] transition-all duration-300 cursor-pointer ${isEmpty ? "top-[10px] text-[14px] text-gray-400" : "-top-2.5 text-xs text-[#A0AEC0] bg-white px-1"}`}
          >
          {placeholder}
          </label>
      </div>
    </div>
  )
}

const FormatInput = React.forwardRef<HTMLInputElement, FieldProps & { editable: boolean; allowZero: boolean }>(
  ({ model, setModel, campoId, type, required, onBlur, min, max, editable, allowZero }, ref) => {
    const formatValue = (value: string | number | undefined | null) => {
      if (value === undefined || value === null || value === "") return ""
      return value.toString()
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        ".",
        ",",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
      ]

      const isNumber = /[0-9]/.test(e.key)

      if (!allowZero && e.key === "0" && !model[campoId]) {
        e.preventDefault()
        return
      }

      if (!allowedKeys.includes(e.key) && !isNumber) {
        e.preventDefault()
      }

      if (
        (e.key === "." || e.key === ",") &&
        (model[campoId]?.toString().includes(".") || model[campoId]?.toString().includes(","))
      ) {
        e.preventDefault()
      }
    }

    return (
      <CurrencyInput
        autoComplete="off"
        id={campoId}
        name={campoId}
        prefix={type === InputTypes.Money ? "$  " : ""}
        allowDecimals={type === InputTypes.Money || type === InputTypes.Number}
        decimalsLimit={2}
        decimalSeparator=","
        groupSeparator="."
        value={formatValue(model[campoId])}
        onValueChange={(value, _) => {
          if (value === undefined || value === "") {
            setModel((prevModel: any) => ({
              ...prevModel,
              [campoId]: "",
            }))
            return
          }

          const numValue = Number.parseFloat(value)

          if (isNaN(numValue)) {
            setModel((prevModel: any) => ({
              ...prevModel,
              [campoId]: "",
            }))
            return
          }

          let limitedValue = numValue
          if (min !== undefined && numValue < min) {
            limitedValue = min
          }
          if (max !== undefined && numValue > max) {
            limitedValue = max
          }

          setModel((prevModel: any) => ({
            ...prevModel,
            [campoId]: limitedValue.toString(),
          }))
        }}
        className="w-full h-[40px] font-normal text-[#171923] p-3 rounded-[10px] border cursor-pointer peer"
        placeholder=""
        required={required}
        onBlur={onBlur}
        disabled={!editable}
        onKeyDown={handleKeyDown}
        ref={ref as React.RefObject<HTMLInputElement>}
      />
    )
  },
)

export default MoneyField
